// Filter Panel Component

import { useState } from 'react';
import { useMapStore } from '../../stores';
import type { LeadFilter } from '../../types';
import { cn, getLeadTypeLabel, getLeadTypeColor, getCategoryIcon } from '../../lib/utils';
import { X, ChevronDown, ChevronUp, Globe, AlertTriangle, Target, Star, MessageCircle, Building2, MapPin, SlidersHorizontal } from 'lucide-react';

interface FilterPanelProps {
  filters: LeadFilter;
  onChange: (filters: Partial<LeadFilter>) => void;
  onClose: () => void;
  leadCounts: Record<string, number>;
}

const CATEGORIES = [
  'restaurant', 'cafe', 'bar', 'retail', 'health', 'beauty', 'fitness',
  'automotive', 'home', 'professional', 'education', 'entertainment',
  'travel', 'finance', 'legal', 'real-estate', 'technology', 'construction'
];

const LEAD_TYPES = [
  { value: 'no-website', label: 'No Website', icon: Globe, color: 'text-red-600 bg-red-100' },
  { value: 'marketing', label: 'Needs Marketing', icon: AlertTriangle, color: 'text-amber-600 bg-amber-100' },
  { value: 'b2b', label: 'B2B Lead', icon: Target, color: 'text-blue-600 bg-blue-100' },
  { value: 'multiple', label: 'Multiple Needs', icon: AlertTriangle, color: 'text-purple-600 bg-purple-100' },
];

const RATING_OPTIONS = [
  { value: 0, label: 'Any Rating' },
  { value: 4, label: '4+ Stars' },
  { value: 3.5, label: '3.5+ Stars' },
  { value: 3, label: '3+ Stars' },
];

const REVIEW_OPTIONS = [
  { value: 0, label: 'Any Reviews' },
  { value: 10, label: '10+' },
  { value: 50, label: '50+' },
  { value: 100, label: '100+' },
  { value: 500, label: '500+' },
];

export function FilterPanel({ filters, onChange, onClose, leadCounts }: FilterPanelProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    categories: true,
    leadTypes: true,
    leadScore: true,
    website: true,
    rating: false,
    reviews: false,
    distance: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="border-b border-slate-200/50 bg-white/50">
      <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
        {/* Categories */}
        <div className="border border-slate-200/50 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('categories')}
            className="w-full p-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-600" />
              <span className="font-medium text-slate-900">Categories</span>
              <span className="badge badge-primary">{filters.categories.length}</span>
            </div>
            {expandedSections.categories ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          
          {expandedSections.categories && (
            <div className="p-3 space-y-2 max-h-60 overflow-y-auto">
              {CATEGORIES.map(category => {
                const count = leadCounts[category] || 0;
                const isSelected = filters.categories.includes(category);
                return (
                  <label key={category} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const newCategories = isSelected
                          ? filters.categories.filter(c => c !== category)
                          : [...filters.categories, category];
                        onChange({ categories: newCategories });
                      }}
                      className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500 focus:ring-2"
                    />
                    <span className="text-lg">{getCategoryIcon(category)}</span>
                    <span className="flex-1 font-medium text-slate-700 capitalize">{category}</span>
                    <span className="badge badge-info text-xs">{count}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Lead Types */}
        <div className="border border-slate-200/50 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('leadTypes')}
            className="w-full p-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-slate-600" />
              <span className="font-medium text-slate-900">Lead Types</span>
              <span className="badge badge-primary">{filters.leadTypes.length}</span>
            </div>
            {expandedSections.leadTypes ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          
          {expandedSections.leadTypes && (
            <div className="p-3 space-y-2">
              {LEAD_TYPES.map(({ value, label, icon: Icon, color }) => {
                const count = leadCounts[value] || 0;
                const isSelected = filters.leadTypes.includes(value as any);
                return (
                  <label key={value} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const newTypes = isSelected
                          ? filters.leadTypes.filter(t => t !== value)
                          : [...filters.leadTypes, value as any];
                        onChange({ leadTypes: newTypes });
                      }}
                      className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500 focus:ring-2"
                    />
                    <Icon className={cn('w-5 h-5', color)} />
                    <span className="flex-1 font-medium text-slate-700">{label}</span>
                    <span className="badge badge-info text-xs">{count}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Lead Score Range */}
        <div className="border border-slate-200/50 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('leadScore')}
            className="w-full p-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-slate-600" />
              <span className="font-medium text-slate-900">Lead Score</span>
            </div>
            {expandedSections.leadScore ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          
          {expandedSections.leadScore && (
            <div className="p-3 space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">Min: {filters.minLeadScore}</span>
                  <span className="text-slate-600">Max: {filters.maxLeadScore}</span>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={filters.minLeadScore}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (val <= filters.maxLeadScore) {
                        onChange({ minLeadScore: val });
                      }
                    }}
                    className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none accent-primary-600 cursor-pointer"
                  />
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={filters.maxLeadScore}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (val >= filters.minLeadScore) {
                        onChange({ maxLeadScore: val });
                      }
                    }}
                    className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none accent-primary-600 cursor-pointer"
                  />
                </div>
              </div>
              
              {/* Quick presets */}
              <div className="flex gap-2 flex-wrap">
                {[
                  { label: 'Hot (80+)', min: 80, max: 100 },
                  { label: 'Warm (60-79)', min: 60, max: 79 },
                  { label: 'Cool (40-59)', min: 40, max: 59 },
                  { label: 'All', min: 0, max: 100 },
                ].map(preset => (
                  <button
                    key={preset.label}
                    onClick={() => onChange({ minLeadScore: preset.min, maxLeadScore: preset.max })}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                      filters.minLeadScore === preset.min && filters.maxLeadScore === preset.max
                        ? 'bg-primary-500 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Website Status */}
        <div className="border border-slate-200/50 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('website')}
            className="w-full p-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-slate-600" />
              <span className="font-medium text-slate-900">Website Status</span>
            </div>
            {expandedSections.website ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          
          {expandedSections.website && (
            <div className="p-3 space-y-2">
              {[
                { value: null, label: 'All', key: 'all' },
                { value: false, label: 'No Website', color: 'bg-red-100 text-red-800', key: 'false' },
                { value: true, label: 'Has Website', color: 'bg-green-100 text-green-800', key: 'true' },
              ].map(opt => (
                <label key={opt.key} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                  <input
                    type="radio"
                    name="website-filter"
                    checked={filters.hasWebsite === opt.value}
                    onChange={() => onChange({ hasWebsite: opt.value })}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 focus:ring-2 border-slate-300"
                  />
                  <span className="font-medium text-slate-700">{opt.label}</span>
                  {opt.color && <span className={cn('badge text-xs', opt.color)}>{opt.label}</span>}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Rating */}
        <div className="border border-slate-200/50 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('rating')}
            className="w-full p-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-slate-600 fill-amber-400" />
              <span className="font-medium text-slate-900">Minimum Rating</span>
              {filters.minRating > 0 && <span className="badge badge-warning">{filters.minRating}+</span>}
            </div>
            {expandedSections.rating ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          
          {expandedSections.rating && (
            <div className="p-3 space-y-2">
              {RATING_OPTIONS.map(opt => (
                <label key={opt.value} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                  <input
                    type="radio"
                    name="rating-filter"
                    checked={filters.minRating === opt.value}
                    onChange={() => onChange({ minRating: opt.value })}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 focus:ring-2 border-slate-300"
                  />
                  <span className="font-medium text-slate-700">{opt.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Reviews */}
        <div className="border border-slate-200/50 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('reviews')}
            className="w-full p-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-slate-600" />
              <span className="font-medium text-slate-900">Minimum Reviews</span>
              {filters.minReviews > 0 && <span className="badge badge-info">{filters.minReviews}+</span>}
            </div>
            {expandedSections.reviews ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          
          {expandedSections.reviews && (
            <div className="p-3 space-y-2">
              {REVIEW_OPTIONS.map(opt => (
                <label key={opt.value} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                  <input
                    type="radio"
                    name="reviews-filter"
                    checked={filters.minReviews === opt.value}
                    onChange={() => onChange({ minReviews: opt.value })}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 focus:ring-2 border-slate-300"
                  />
                  <span className="font-medium text-slate-700">{opt.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Distance */}
        <div className="border border-slate-200/50 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('distance')}
            className="w-full p-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-slate-600" />
              <span className="font-medium text-slate-900">Search Radius</span>
              <span className="badge badge-primary">{Math.round(filters.radius / 1000)}km</span>
            </div>
            {expandedSections.distance ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          
          {expandedSections.distance && (
            <div className="p-3 space-y-4">
              <div>
                <input
                  type="range"
                  min="500"
                  max="50000"
                  step="500"
                  value={filters.radius}
                  onChange={(e) => onChange({ radius: parseInt(e.target.value) })}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none accent-primary-600 cursor-pointer"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>500m</span>
                  <span>50km</span>
                </div>
              </div>
              
              <div className="flex gap-2 flex-wrap">
                {[
                  { label: '500m', value: 500 },
                  { label: '1km', value: 1000 },
                  { label: '2km', value: 2000 },
                  { label: '5km', value: 5000 },
                  { label: '10km', value: 10000 },
                  { label: '25km', value: 25000 },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => onChange({ radius: opt.value })}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                      filters.radius === opt.value
                        ? 'bg-primary-500 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2 border-t border-slate-200/50">
          <button
            onClick={onClose}
            className="flex-1 btn-secondary"
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span>Apply Filters</span>
          </button>
          <button
            onClick={() => onChange({
              categories: [],
              leadTypes: ['no-website', 'marketing', 'b2b', 'multiple'],
              minLeadScore: 0,
              maxLeadScore: 100,
              hasWebsite: null,
              minRating: 0,
              minReviews: 0,
              radius: 5000,
            })}
            className="btn-ghost text-slate-500 hover:text-slate-700"
          >
            <X className="w-4 h-4" />
            <span>Reset</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default FilterPanel;